import SPHLogo from "@assets/header/newIcons/bh-logo.svg";
import SPHLogoMobile from "@assets/header/newIcons/bh-logo-mobile.svg";
import MyBHLogo from "@assets/header/newIcons/mybh-logo.svg";
import { useStickyBarLayout } from "@components/Blocks/ArticleStickyBar/hooks/useStickyBarLayout";
import { MiscLinks } from "@components/Modules/HeaderNew/MiscLinks";
import { SectionQuickLinks } from "@components/Modules/HeaderNew/SectionQuickLinks";
import { MYBH_LINK } from "@config/followTopic";
import NDRBanner from "@elements/Banner/NDRBanner";
import {
  IS_PLAYWRIGHT_TEST,
  NDR_FEATURE_ENABLE,
  QUICKNEWS_FLAG,
} from "@helper/getEnvVariables";
import { useEshopSubscribeCta } from "@helper/useEshopSubscribeCta";
import useUserInfo from "@hooks/useUserInfo";
import { NoSSR } from "@sphtech/web2-core/components";
import type { ReactElement } from "react";
import { useRef, useState } from "react";
import { useLocation } from "react-router";

import { QnHeaderIcon } from "../QuickNews/QnButtons";
import { useHeaderScroll } from "./hooks/useHeaderScroll";
import { MobileMenuIcon } from "./MobileMenuIcon";
import { MobileMenuModal } from "./MobileMenuModal";
import MySphLoginButton from "./MySphLoginButton";
import MosqueButton from "./PrayerTime/MosqueButton";
import PrayerTimeNew from "./PrayerTime/PrayerTimeNew";
import { SearchButton } from "./Search/SearchButton";
import { SearchInput } from "./Search/SearchInput";
import UserInfo from "./UserInfo/UserInfo";

const SeparatorLine = (): ReactElement => (
  <div className="w-[1px] h-[26px] bg-neutral-500 opacity-50" />
);

export type HeaderProps = Readonly<{
  testId?: string;
  hideNavigationInMobile?: boolean;
  showNDRBanner?: boolean;
  mockUserInfo?: ReturnType<typeof useUserInfo>["userInfo"];
}>;

export function HeaderNewV2({
  testId = "header",
  hideNavigationInMobile = false,
  showNDRBanner,
  mockUserInfo,
}: HeaderProps): ReactElement {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isMobWebView = searchParams.get("mobwebview");

  const { userInfo: realUserInfo } = useUserInfo();
  const userInfo = mockUserInfo ?? realUserInfo;

  const [expanded, setExpanded] = useState(false);
  const [expandMenu, setExpandMenu] = useState(false);
  const [showPrayerTime, setShowPrayerTime] = useState(false);
  const [showPrayerTimeDesktop, setShowPrayerTimeDesktop] = useState(false);

  const barRef = useRef<null>(null);
  const { setLayoutHeights } = useStickyBarLayout(
    barRef,
    `${testId}-outerContainer`,
  );

  const { showMosqueBtn, isDesktopScrolled } = useHeaderScroll(
    hideNavigationInMobile,
    setLayoutHeights,
  );

  const toggleExpanded = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  const toggleMobileMenu = () => {
    setExpandMenu((prevExpandMenu) => !prevExpandMenu);
    setExpanded(false);
  };

  const subscribeCta = useEshopSubscribeCta("subscribe-header");

  return isMobWebView ? (
    <></>
  ) : (
    <>
      <div
        data-testid={`${testId}-outerContainer`}
        id="header-container"
        className="flex flex-col font-primary text-neutral-700 font-sm font-regular h-auto w-full my-0 mx-auto sticky top-0 z-[51]"
      >
        <div className="flex flex-col bg-bg-primary">
          <div
            data-testid={`${testId}-innerContainer`}
            className="lg:max-w-screen-lg xl:max-w-screen-bh-cxl w-full mx-auto h-[39px] lg:h-auto py-0 lg:py-2 pl-2.5 pr-1 lg:pl-0 lg:pr-0 bg-bg-primary"
          >
            <div
              className={`flex h-[40px] ${isDesktopScrolled ? "lg:h-[30px]" : "lg:h-[70px]"} justify-between`}
            >
              <div className="self-center lg:self-end lg:flex lg:justify-start">
                <a
                  href="/"
                  data-testid={`${testId}-logo`}
                  className={`flex w-auto transition-all duration-300 ease-in-out
                    ${isDesktopScrolled ? "lg:w-[175px] lg:h-[30px]" : "lg:w-[338px] lg:h-[58px]"}
                  `}
                >
                  <img
                    src={SPHLogo}
                    alt="SPH Logo"
                    className="w-auto lg:w-full hidden lg:block"
                  />
                  <div className="lg:hidden">
                    <img
                      src={SPHLogoMobile}
                      alt="SPH Logo mobile"
                      width={166}
                      height={30}
                      className="lg:hidden"
                    />
                  </div>
                </a>
              </div>
              <div className="hidden lg:flex justify-end">
                <div className="flex flex-col justify-between h-full relative">
                  <div className="flex self-end">
                    <MiscLinks testId={testId} />
                    {isDesktopScrolled && (
                      <>
                        <MosqueButton
                          variant="withText"
                          onClick={() => {
                            setShowPrayerTimeDesktop((prev) => !prev);
                          }}
                          wrapperClass="flex gap-1 p-0 items-center"
                          clicked={showPrayerTimeDesktop}
                        />
                        <span className="px-xxs text-neutral-300 text-sm font-regular">
                          |
                        </span>
                      </>
                    )}
                    {userInfo ? (
                      <div className="relative">
                        <UserInfo
                          rootClassName="flex items-center"
                          userInfo={userInfo}
                        />
                      </div>
                    ) : (
                      <MySphLoginButton
                        buttonStyle="text-[14px] text-brand-primary"
                        iconWidth="12px"
                        iconHeight="12px"
                      />
                    )}
                    {subscribeCta.isSubscribeAvailable && (
                      <>
                        <span className="px-xxs text-neutral-300 text-sm font-regular">
                          |
                        </span>
                        <a
                          href={subscribeCta.href}
                          onClick={subscribeCta.onClick}
                          className="text-xs text-text-inverse font-regular text-center bg-blue-550 rounded-[20px] min-w-[120px] xl:w-[150px]"
                        >
                          Langgan
                        </a>
                      </>
                    )}
                  </div>
                  <NoSSR>
                    <div
                      className={`hidden self-end items-end
                      transition-all duration-300 ease-in-out
                      ${
                        isDesktopScrolled
                          ? showPrayerTimeDesktop
                            ? "lg:flex h-auto opacity-100 absolute top-7 right-1/3 z-10 bg-neutral-200 p-2.5"
                            : "lg:hidden h-0 opacity-0"
                          : "lg:flex h-auto opacity-100 -mb-1"
                      }
                      `}
                    >
                      <PrayerTimeNew
                        currentDate={new Date().toLocaleDateString("en-GB")}
                      />
                    </div>
                  </NoSSR>
                </div>
              </div>
              <div className="basis-3/6 flex w-[100px] h-[39px] justify-end items-center lg:hidden">
                <a href={`/${MYBH_LINK}?ref=mybh-menu`} className="p-2.5">
                  <img src={MyBHLogo} alt="MyBH Logo" width={43} />
                </a>
                <SeparatorLine />
                {IS_PLAYWRIGHT_TEST !== "true" && QUICKNEWS_FLAG && (
                  <>
                    <QnHeaderIcon variant="blue" />
                    <SeparatorLine />
                    <div
                      data-testid="mobile-mosque-btn-wrapper"
                      className={`
                        overflow-hidden
                        transition-all duration-300 ease-in-out
                        ${showMosqueBtn ? "w-[41px]" : "w-0"}
                      `}
                      aria-hidden={!showMosqueBtn}
                    >
                      <div
                        className={`
                        flex items-center
                        transition-all duration-500 ease-in-out
                        transform origin-right
                        ${
                          showMosqueBtn
                            ? "opacity-100 translate-x-0"
                            : "opacity-0 pointer-events-none translate-x-4"
                        }
                        ${showPrayerTime && "bg-neutral-200"}
                      `}
                      >
                        <MosqueButton
                          onClick={() => {
                            setShowPrayerTime((prev) => !prev);
                          }}
                          tabIndex={showMosqueBtn ? 0 : -1}
                        />
                        <SeparatorLine />
                      </div>
                    </div>
                  </>
                )}
                <MobileMenuIcon
                  showMenuIcon={true}
                  onClick={toggleMobileMenu}
                  variant="blue"
                />
              </div>
            </div>
          </div>
          {showMosqueBtn && (
            <NoSSR>
              <div
                data-testid="mobile-prayer-wrapper"
                className={`bg-neutral-200 ${showPrayerTime ? "h-[42px] opacity-100" : "h-0 opacity-0 z-0 pointer-events-none"} transition-all ease-in-out duration-300`}
              >
                <PrayerTimeNew
                  currentDate={new Date().toLocaleDateString("en-GB")}
                />
              </div>
            </NoSSR>
          )}
          <div
            className={`${hideNavigationInMobile ? "hidden lg:flex" : "flex"} flex-col`}
          >
            <div className="flex items-center w-full">
              <div className="flex items-center lg:hidden">
                <SearchButton
                  expanded={expanded}
                  onClick={toggleExpanded}
                  isMobile={true}
                  variant="dark"
                />
                <div className="mr-2 flex-shrink-0">
                  <SeparatorLine />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <SectionQuickLinks testId={testId} />
              </div>
            </div>
            <div className="lg:hidden">
              <SearchInput expanded={expanded} isMobile={true} />
            </div>
          </div>
        </div>
      </div>
      <div
        data-testid="mobile-foodmap-prayertime"
        className={hideNavigationInMobile ? "hidden" : "lg:hidden"}
      >
        <NoSSR>
          <div className="flex items-center justify-center h-[42px] bg-[#FAFAFA]">
            <PrayerTimeNew
              currentDate={new Date().toLocaleDateString("en-GB")}
            />
          </div>
        </NoSSR>
      </div>
      {showNDRBanner && NDR_FEATURE_ENABLE ? <NDRBanner /> : <></>}

      <MobileMenuModal
        expandMenu={expandMenu}
        onClick={toggleMobileMenu}
        userInfo={userInfo}
      />
    </>
  );
}
